// Admin self-monitoring strip — surfaces the timestamp + IP + UA of the
// previous admin session so the admin can spot unfamiliar logins.
//
// Display logic:
// - "Logged in just now" for the current session (already implied — they're here).
// - "Previous session: <relative time> from <ip>" if we have a prior login.
// - On first-ever login, the previous block is hidden gracefully.

import React, { useEffect, useState } from "react";
import { Clock, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

function shortUA(ua) {
  if (!ua) return "";
  // Keep it readable — extract browser + OS from common UAs.
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Edg|Opera)\/([\d.]+)/);
  let browser = m ? m[1].replace("Edg", "Edge") : "Browser";
  let os = "";
  if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/(iPhone|iPad)/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return os ? `${browser} on ${os}` : browser;
}

export default function AdminLastLoginBadge() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/last-login");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err || !data) return null;

  const current = data.current;
  const prev = data.previous;

  return (
    <div
      data-testid="admin-last-login"
      className="card-surface p-4 md:p-5 flex flex-wrap items-center gap-x-6 gap-y-2 bg-gradient-to-br from-[#15110b] to-[#0E1014]"
    >
      <div className="flex items-center gap-2 text-sm text-[#A1A4AB]">
        <Clock className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
        <span className="overline text-[#C5A059] text-xs">Session</span>
        {current ? (
          <>
            <span className="text-[#FDFBF7]" data-testid="admin-current-login-time">
              Logged in {timeAgo(current.at)}
            </span>
            <span className="text-[#6C707A]">·</span>
            <span className="text-[#A1A4AB]" data-testid="admin-current-login-ip">
              {current.ip || "—"}
            </span>
            {current.ua && (
              <>
                <span className="text-[#6C707A]">·</span>
                <span className="text-[#A1A4AB]">{shortUA(current.ua)}</span>
              </>
            )}
          </>
        ) : (
          <span className="text-[#A1A4AB]">First login of this account.</span>
        )}
      </div>

      {prev && (
        <div className="flex items-center gap-2 text-sm text-[#A1A4AB]" data-testid="admin-previous-login">
          <ShieldAlert className="w-4 h-4 text-[#A1A4AB]" strokeWidth={1.5} />
          <span className="overline text-[#A1A4AB] text-xs">Previous session</span>
          <span className="text-[#FDFBF7]">{timeAgo(prev.at)}</span>
          <span className="text-[#6C707A]">·</span>
          <span>{prev.ip || "—"}</span>
          {prev.ua && (
            <>
              <span className="text-[#6C707A]">·</span>
              <span>{shortUA(prev.ua)}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
